"use client";

import { useEffect, useRef } from "react";

import { useAnalytics } from "./useAnalytics";

export function useSessionTracking() {
  const analytics = useAnalytics();
  const sessionStartRef = useRef<number>(Date.now());
  const sessionIdRef = useRef<string>(generateSessionId());
  const lastActivityRef = useRef<number>(Date.now());
  const inactivityTimeoutRef = useRef<NodeJS.Timeout>();

  function generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(7)}`;
  }

  useEffect(() => {
    analytics.trackSessionStart();

    const lastSessionDate = localStorage.getItem("lastSessionDate");
    if (lastSessionDate) {
      const daysSinceLastSession = Math.floor(
        (Date.now() - parseInt(lastSessionDate)) / (1000 * 60 * 60 * 24),
      );

      if (daysSinceLastSession >= 1) {
        analytics.trackUserReturn();
      }
    }

    localStorage.setItem("lastSessionDate", Date.now().toString());
    localStorage.setItem("currentSessionId", sessionIdRef.current);

    return () => {
      // cleanup
    };
  }, [analytics]);

  useEffect(() => {
    const sessionStartTime = sessionStartRef.current;

    const handleBeforeUnload = () => {
      const sessionDuration = Math.round(
        (Date.now() - sessionStartTime) / 1000,
      );
      analytics.trackSessionEnd(sessionDuration);
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [analytics]);

  useEffect(() => {
    const handleUserActivity = () => {
      lastActivityRef.current = Date.now();

      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }

      inactivityTimeoutRef.current = setTimeout(
        () => {
          const timeSinceLastActivity = Math.round(
            (Date.now() - lastActivityRef.current) / 1000,
          );

          analytics.trackUserEvent("user_inactive" as never, {
            inactiveForSeconds: timeSinceLastActivity,
            sessionId: sessionIdRef.current,
          });
        },
        5 * 60 * 1000,
      );
    };

    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach((event) => {
      window.addEventListener(event, handleUserActivity);
    });

    return () => {
      if (inactivityTimeoutRef.current) {
        clearTimeout(inactivityTimeoutRef.current);
      }
      events.forEach((event) => {
        window.removeEventListener(event, handleUserActivity);
      });
    };
  }, [analytics]);

  return {
    sessionId: sessionIdRef.current,
    sessionStartTime: sessionStartRef.current,
  };
}

export function useChurnDetection() {
  const analytics = useAnalytics();

  useEffect(() => {
    const lastActivityDate = localStorage.getItem("lastActivityDate");
    if (lastActivityDate) {
      const daysSinceLastActivity = Math.floor(
        (Date.now() - parseInt(lastActivityDate)) / (1000 * 60 * 60 * 24),
      );

      if (daysSinceLastActivity >= 60) {
        const userEmail = localStorage.getItem("userEmail");
        analytics.trackChurn(`Inactive for ${daysSinceLastActivity} days`, {
          userEmail,
          daysSinceLastActivity,
          lastActivityDate: new Date(parseInt(lastActivityDate)).toISOString(),
        });
      }
    }

    localStorage.setItem("lastActivityDate", Date.now().toString());
  }, [analytics]);

  return null;
}
